import { useState } from 'react';
import { BookOpen, Check, Loader2, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

import { useAgentKnowledgeBasesQuery, useLinkKnowledgeBaseMutation } from '@/query/agent.query';

import useModalStore from '@/store/modal.store';
import type { AgentKnowledgeBase } from '@/types/agent.type';

type SelectKnowledgeBaseContentProps = {
  agentId: number;
};

export function SelectKnowledgeBaseContent({ agentId }: SelectKnowledgeBaseContentProps) {
  const [search, setSearch] = useState('');
  const [selectedKb, setSelectedKb] = useState<AgentKnowledgeBase | null>(null);

  const { data: availableKbs = [], isLoading } = useAgentKnowledgeBasesQuery(agentId, false);

  const linkMutation = useLinkKnowledgeBaseMutation(agentId, {
    onSuccess: () => useModalStore.getState().closeModal(),
  });

  const filteredKbs = search
    ? availableKbs.filter(kb => kb.name.toLowerCase().includes(search.toLowerCase()))
    : availableKbs;

  const handleConfirm = () => {
    if (!selectedKb) return;
    linkMutation.mutate(selectedKb.id);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add knowledge base</DialogTitle>
        <DialogDescription>Select a knowledge base to link to this agent.</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search knowledge bases..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[280px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredKbs.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {availableKbs.length === 0
                ? 'All knowledge bases are already linked.'
                : 'No knowledge bases match your search.'}
            </div>
          ) : (
            <div className="space-y-1 pr-3">
              {filteredKbs.map(kb => {
                const isSelected = selectedKb?.id === kb.id;
                return (
                  <button
                    key={kb.id}
                    type="button"
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => setSelectedKb(isSelected ? null : kb)}
                  >
                    <BookOpen
                      className={`h-5 w-5 shrink-0 ${
                        isSelected ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{kb.name}</div>
                      {kb.kb_description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {kb.kb_description}
                        </div>
                      )}
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => useModalStore.getState().closeModal()}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!selectedKb || linkMutation.isPending}>
          {linkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Link knowledge base
        </Button>
      </DialogFooter>
    </>
  );
}
